import React, { useState, useEffect, useRef } from 'react'
import styled, { keyframes, css } from 'styled-components'
import CheckIcon from '@mui/icons-material/Check'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import { useConfig } from '@/contexts/ConfigContext'
import CodeIcon from '@mui/icons-material/Code'
import LockIcon from '@mui/icons-material/Lock'
import { Tooltip } from '@mui/material'

import { useLanguage } from '@/contexts/LanguageContext'
import { useModuleView } from '@/contexts/ModuleViewContext'
import { COMING_SOON_MODULES } from '@/utils/constants'
import { usePro } from '@/contexts/ProContext'


const shake = keyframes`
  0%, 100% { transform: translateX(0); }
  20% { transform: translateX(-2px); }
  40% { transform: translateX(2px); }
  60% { transform: translateX(-2px); }
  80% { transform: translateX(2px); }
`

const stampIn = keyframes`
    from { opacity: 0; transform: scale(1.5) rotate(-10deg); }
    to { opacity: 1; transform: scale(1) rotate(-15deg); }
`

const LockedOverlay = styled.div`
    position: absolute;
    top: -10px;
    left: -18px;
    right: -18px;
    bottom: -16px;
    background: transparent;
    z-index: 100;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    pointer-events: all;
    border-radius: 0 0 7px 7px;
    overflow: hidden;
    cursor: pointer;
    transition: all 0.2s ease;
`

const LockedStamp = styled.div`
    font-size: 5rem;
    font-weight: 950;
    color: rgba(255, 0, 221, 0.4);
    text-transform: uppercase;
    transform: rotate(-15deg);
    user-select: none;
    letter-spacing: 12px;
    border: 12px solid rgba(255, 0, 221, 0.25);
    padding: 15px 35px;
    border-radius: 12px;
    white-space: nowrap;
    animation: ${stampIn} 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards;
    box-shadow: 0 0 40px rgba(255, 0, 221, 0.1);
`

const ModulesContainer = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 16px 18px;
    background-color: ${({ isLocked, isActive }) =>
        isLocked
            ? 'rgba(40, 30, 60, 0.4)'
            : isActive
                ? 'rgba(73, 74, 181, 0.15)'
                : 'rgba(22, 15, 30, 0.25)'};
    border-radius: 7px;
    border: 1px solid ${({ isActive }) =>
        isActive ? '#6365f189' : 'rgba(255, 255, 255, 0.05)'};
    //max-width: 900px;
    width: 100%;
    transition: all 0.2s ease-in-out;
    box-shadow: ${({ isActive }) =>
        isActive
            ? '0 4px 16px rgba(0, 0, 0, 0.15)'
            : '0 2px 4px rgba(0, 0, 0, 0.05)'};
    scroll-margin-top: 20px;
    transition: all 0.2s ease-in-out;
`

const Header = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    cursor: pointer;
    user-select: none;
    padding-right: 10px;

`

const TitleSection = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    flex-grow: 1;
    padding-left: 10px;
    padding-right: 10px;

    svg {
        color: #666;
        height: 20px;
        width: 20px;
    }
`

const Title = styled.h3`
    font-size: 1.2rem;
    font-weight: 400;
    color: #aaa;
    text-transform: uppercase;
    margin: 0;
    ${({ isActive }) => isActive && 'color: white;'}
`

const CheckboxButton = styled.div`
    width: 24px;
    height: 24px;
    border-radius: 50%;
    border: 2px solid ${({ isLocked }) => (isLocked ? 'rgba(255, 255, 255, 0.05)' : '#ffffff1b')};
    background-color: ${({ isActive, isLocked }) => {
        if (isLocked) return isActive ? 'rgba(99, 102, 241, 0.2)' : 'transparent'
        return isActive ? '#6366f1' : '#5f5f5f8d'
    }};
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: ${({ isLocked }) => (isLocked ? 'not-allowed' : 'pointer')};
    pointer-events: ${({ isLocked }) => (isLocked ? 'none' : 'auto')};
    opacity: 1;
    transition: all 0.2s ease;
    flex-shrink: 0;
    box-shadow: ${({ isActive }) => isActive ? '0 0 10px rgba(99, 102, 241, 0.3)' : 'none'};

    &:hover {
        border-color: ${({ isActive, isLocked }) => {
        if (isLocked) return 'rgba(255, 255, 255, 0.05)'
        return isActive ? '#6366f1' : '#888'
    }};
    }
`

const ReadOnlyBadge = styled.div`
    font-size: 10px;
    color: #6366f1;
    border: 1px solid rgba(255, 255, 255, 0.05);
    background: rgba(99, 102, 241, 0.15);
    padding: 2px 8px;
    border-radius: 4px;
    font-weight: 800;
    letter-spacing: 0.5px;
    white-space: nowrap;
    display: flex;
    align-items: center;
    gap: 4px;
    margin-left: 10px;
    animation: ${({ $shouldShake }) => $shouldShake ? shake : 'none'} 0.4s cubic-bezier(.36,.07,.19,.97) both;
    pointer-events: none;
`

const StyledLockIcon = styled(LockIcon)`
    font-size: 20px !important;
    color: #ff00ddff;
    animation: ${({ $shouldShake }) => ($shouldShake ? css`${shake} 0.4s` : 'none')};
`

const ChevronButton = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    color: #aaa;
    transition: color 0.2s;
    margin-left: 0;

    &:hover {
        color: white;
    }
`

const Content = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    margin-top: 5px;
    padding-top: 10px;
    border-top: 1px solid rgba(255, 255, 255, 0.05);
    animation: fadeIn 0.3s ease;
    min-height: ${({ $minHeight }) => $minHeight || 'auto'};
    min-width: ${({ $minWidth }) => $minWidth || 'auto'};
    transition: all 0.2s ease-in-out;

    @keyframes fadeIn {
        from {
            opacity: 0;
            transform: translateY(-5px);
        }
        to {
            opacity: 1;
            transform: translateY(0);
        }
    }
`

const CopyButton = styled.div`
    position: absolute;
    right: 17px;
    top: 22px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 2px;
    background: ${({ copied }) =>
        copied ? 'rgba(74, 222, 128, 0.2)' : 'rgba(255, 255, 255, 0.05)'};
    border: 1px solid
        ${({ copied }) =>
        copied ? 'rgba(74, 222, 128, 0.4)' : 'rgba(255, 255, 255, 0.1)'};
    padding: 0 8px;
    border-radius: 6px;
    cursor: pointer;
    color: ${({ copied }) => (copied ? '#4ade80' : '#aaa')};
    transition: all 0.2s ease;
    z-index: 10;
    height: 32px;
    //width: 52px;
    font-size: 10px;
    font-weight: bold;
    text-transform: uppercase;
    
    &:hover {
        background: ${({ copied }) =>
        copied ? 'rgba(74, 222, 128, 0.3)' : 'rgba(255, 255, 255, 0.15)'};
        color: ${({ copied }) => (copied ? '#4ade80' : 'white')};
        transform: scale(1.05);
    }

    svg {
        width: 12px;
        height: 12px;
        margin-right: 4px;
    }
`

const fadeIn = keyframes`
    from { opacity: 0; }
    to { opacity: 1; }
`

const ComingSoonStamp = styled(LockedStamp)`
    color: rgba(251, 191, 36, 0.8);
    border-color: rgba(251, 191, 36, 0.4);
    box-shadow: 0 0 40px rgba(251, 191, 36, 0.15);
    font-size: 2.8rem;
    padding: 15px 30px;
`

import { useAuth } from '@/contexts/AuthContext'
// Removed DemoModal import as functionality is replaced by simple overlay

export default function ModuleContainer({
    id,
    moduleKey,
    title,
    description,
    icon: Icon,
    children,
    initialExpanded = false,
    minHeight,
    minWidth,
    headerActions,
    isComingSoon = false
}) {
    const {
        activeModules,
        toggleModule,
        toggleSignal,
        getModuleCommands,
        focusSignal,
        loadSignal,
        configIsLocked,
        handleLockedAction,
        lockedFeedbackSignal
    } = useConfig()
    const { isDashboard, navigateToManager } = useModuleView()
    const { hasFeature } = usePro()

    // Los módulos avanzados solo se destraban con acceso pago.
    const puedeAvanzados = hasFeature('modulos_avanzados')
    const isUnderConstruction = !puedeAvanzados && (isComingSoon || COMING_SOON_MODULES.includes(moduleKey))

    const isActive = isUnderConstruction ? false : activeModules.includes(moduleKey)
    const [isExpanded, setIsExpanded] = useState(isActive || initialExpanded)
    const [copied, setCopied] = useState(false)
    const containerRef = useRef(null)
    const { t } = useLanguage()

    // Handle loadSignal: when a config is loaded/reset, always start collapsed
    useEffect(() => {
        if (loadSignal > 0 && !isDashboard) {
            setIsExpanded(false)
        }
    }, [loadSignal, isDashboard])

    // Collapse/Expand when signal is received
    useEffect(() => {
        if (toggleSignal.id > 0) {
            setIsExpanded(toggleSignal.expand)
        }
    }, [toggleSignal])

    // Focus signal handler
    useEffect(() => {
        if (focusSignal.id > 0 && focusSignal.moduleKey === moduleKey) {
            if (focusSignal.expand) {
                setIsExpanded(true)
            }
            setTimeout(() => {
                containerRef.current?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start',
                })
            }, 100)
        }
    }, [focusSignal, moduleKey])

    const [shakeBadge, setShakeBadge] = useState(false)

    useEffect(() => {
        if (lockedFeedbackSignal > 0 && configIsLocked) {
            setShakeBadge(true)
            const timer = setTimeout(() => setShakeBadge(false), 600)
            return () => clearTimeout(timer)
        }
    }, [lockedFeedbackSignal, configIsLocked])

    const prevActiveRef = useRef(isActive)

    // Auto-collapse/expand when active state changes
    useEffect(() => {
        if (prevActiveRef.current === false && isActive === true) {
            setIsExpanded(true)
        } else if (prevActiveRef.current === true && isActive === false) {
            setIsExpanded(false)
        }
        prevActiveRef.current = isActive
    }, [isActive])

    const handleCheckboxClick = (e) => {
        e.stopPropagation()
        if (isUnderConstruction) {
            return
        }
        if (configIsLocked) {
            handleLockedAction()
            return
        }
        toggleModule(moduleKey)
    }

    const handleHeaderClick = () => {
        setIsExpanded(!isExpanded)
    }

    const handleContentClick = () => {
        if (isUnderConstruction) {
            return
        }
        if (configIsLocked) {
            handleOverlayClick()
            return
        }
        if (!isActive) {
            toggleModule(moduleKey)
        }
    }

    const handleOverlayClick = (e) => {
        if (e) e.stopPropagation()
        handleLockedAction()
        navigateToManager()
    }

    const hasCommands = getModuleCommands(moduleKey).length > 0

    const handleCopyCode = (e) => {
        e.stopPropagation()
        const commands = getModuleCommands(moduleKey)
        if (commands && commands.length > 0) {
            navigator.clipboard.writeText(commands.join('; '))
            setCopied(true)
            setTimeout(() => setCopied(false), 2000)
        }
    }

    return (
        <ModulesContainer
            id={id}
            isActive={isActive}
            isLocked={configIsLocked}
            ref={containerRef}
            style={{
                ...(isDashboard ? { background: 'transparent', border: 'none', boxShadow: 'none', padding: 0 } : {}),
                ...(isUnderConstruction ? { filter: 'grayscale(85%) brightness(0.85)' } : {}),
            }}
        >

            {!isDashboard && (
                <Header onClick={handleHeaderClick}>
                    <CheckboxButton
                        isActive={isActive}
                        isLocked={configIsLocked}
                        onClick={isUnderConstruction ? undefined : handleCheckboxClick}
                    >
                        {configIsLocked ? (
                            <StyledLockIcon
                                $shouldShake={shakeBadge}
                                style={{ opacity: isActive ? 1 : 0.4 }}
                            />
                        ) : (
                            isActive && (
                                <CheckIcon style={{ fontSize: 12, color: '#ffffff' }} />
                            )
                        )}
                    </CheckboxButton>

                    <TitleSection>
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'baseline',
                                flexDirection: 'column',
                                gap: '10px',
                            }}
                        >
                            <Title isActive={isActive}>{title}</Title>
                            {description && (
                                <span style={{ fontSize: 12, color: 'inherit', opacity: 0.6 }}>
                                    {description}
                                </span>
                            )}
                        </div>
                        {configIsLocked && (
                            <ReadOnlyBadge $shouldShake={shakeBadge}>
                                {t('READ_ONLY')}
                            </ReadOnlyBadge>
                        )}
                        {isUnderConstruction && (
                            <ReadOnlyBadge style={{ color: '#a78bfa', background: 'rgba(167, 139, 250, 0.1)', borderColor: 'rgba(167, 139, 250, 0.25)' }}>
                                {t('COMING_SOON_LABEL') || 'PRO'}
                            </ReadOnlyBadge>
                        )}
                    </TitleSection>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: 'auto', marginRight: '10px', flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
                        {headerActions}
                        {isActive && hasCommands && !isUnderConstruction && (
                            <CopyButton
                                copied={copied}
                                isExpanded={isExpanded}
                                onClick={handleCopyCode}
                                style={{ position: 'static' }}
                                title={t('COPY_TO_CONSOLE_DESC')}
                            >
                                {copied ? (
                                    <CheckIcon style={{ fontSize: 16 }} />
                                ) : (
                                    <CodeIcon style={{ fontSize: 12 }} />
                                )}
                                {t('COPY_TO_CONSOLE_DESC_QUICK')}
                            </CopyButton>
                        )}
                    </div>

                    <ChevronButton>
                        {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </ChevronButton>
                </Header>
            )}

            {(isExpanded || isDashboard) && (
                <Content
                    onClick={handleContentClick}
                    onFocus={handleContentClick}
                    $minHeight={minHeight}
                    $minWidth={minWidth}
                    style={{
                        ...(isDashboard ? { borderTop: 'none', marginTop: 0, paddingTop: 0 } : {}),
                        position: 'relative'
                    }}
                >
                    {configIsLocked && (
                        <LockedOverlay onClick={handleOverlayClick}>
                            <LockedStamp>{t('READ_ONLY')}</LockedStamp>
                        </LockedOverlay>
                    )}

                    <div style={{
                        opacity: configIsLocked ? 0.25 : 1,
                        pointerEvents: configIsLocked ? 'none' : 'auto',
                        filter: !isActive ? 'grayscale(1) brightness(0.55)' : 'none',
                        transition: 'opacity 0.3s ease, filter 0.3s ease',
                        width: '100%'
                    }}>
                        {children}
                    </div>
                </Content>
            )}
        </ModulesContainer>
    )
}
